/**
 * Login Page Component
 *
 * Main authentication page that handles multiple login scenarios:
 * - Standard email/password authentication
 * - New password challenge for first-time users
 * - User details setup after initial login
 *
 * Integrates with AWS Cognito for secure authentication.
 */

import * as React from "react";
import { useNavigate } from "react-router-dom";

import { CircularProgress, Paper, Stack, Typography } from "@mui/material";

import { AuthContext } from "../../context/auth-context";

import LoginForm from "./components/login-form";
import NewPasswordChallengeForm from "./components/new-password-challenge-form";

import { ClearCallLogoTitle } from "../../components/clear-call-logo";
import UserDetailsForm from "../../components/users/user-details-form";
import Copyright from "../../components/copyright";
import ribbonBackground from "../../assets/ribbon.png";
import { RouteType } from "../../router";
import { useGetCurrentUser } from "../../store";

const LoginPage: React.FC = () => {
  const [newPasswordRequired, setNewPasswordRequired] =
    React.useState<boolean>(false);
  const [email, setEmail] = React.useState<string>("");
  const [submissionError, setSubmissionError] = React.useState<string>("");
  const [hasAuthenticated, setHasAuthenticated] =
    React.useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  const { authenticateUser, completeNewPasswordChallenge } =
    React.useContext(AuthContext);
  const { data: currentUser, isLoading: isCurrentUserLoading } =
    useGetCurrentUser();

  const navigate = useNavigate();

  const requiresUserDetails =
    currentUser !== null &&
    currentUser !== undefined &&
    (!currentUser.fullName?.trim() || !currentUser.jobTitle?.trim());

  React.useEffect(() => {
    if (!hasAuthenticated || isCurrentUserLoading || !currentUser) {
      return;
    }

    if (!requiresUserDetails) {
      navigate(RouteType.Calendar);
    }
  }, [
    currentUser,
    hasAuthenticated,
    isCurrentUserLoading,
    navigate,
    requiresUserDetails,
  ]);

  /**
   * Handles user authentication and navigation based on login state
   * @param email - User's email address
   * @param password - User's password
   */
  const redirectOnAuthenticateUser = (email: string, password: string) => {
    setIsSubmitting(true);
    authenticateUser(email, password, {
      onSuccess: () => {
        setSubmissionError("");
        setHasAuthenticated(true);
        setIsSubmitting(false);
      },
      onFailure: (err) => {
        const errorMessage =
          err instanceof Error ? err.message : "Invalid email or password";

        setSubmissionError(
          errorMessage.includes(
            "Temporary password has expired and must be reset by an administrator",
          )
            ? errorMessage
            : "Invalid email or password",
        );
        setHasAuthenticated(false);
        setIsSubmitting(false);
      },
      newPasswordRequired: () => {
        setEmail(email);
        setNewPasswordRequired(true);
        setSubmissionError("");
        setHasAuthenticated(false);
        setIsSubmitting(false);
      },
    });
  };

  /**
   * Handles new password challenge for first-time users
   * @param password - New password set by user
   */
  const redirectOnNewPasswordChallenge = (password: string) => {
    setIsSubmitting(true);
    completeNewPasswordChallenge(password, {
      onSuccess: () => {
        setSubmissionError("");
        setHasAuthenticated(false);
        setNewPasswordRequired(false);
        redirectOnAuthenticateUser(email, password);
      },
      onFailure: (err) => {
        setIsSubmitting(false);
        setSubmissionError(
          err instanceof Error ? err.message : "Failed to set new password",
        );
      },
    });
  };

  /**
   * Success callback for user details setup
   */
  const onSetDetailsSuccess = () => {
    setIsSubmitting(false);
    navigate(RouteType.Calendar);
  };

  /**
   * Error callback for user details setup
   * @param error - Error that occurred during setup
   */
  const onSetDetailsError = (error: unknown) => {
    console.error(error);
    setIsSubmitting(false);
    navigate(RouteType.Calendar);
  };

  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={{
        minHeight: "100vh",
        backgroundImage: `linear-gradient(160deg, rgba(0, 99, 102, 0.73), rgba(0, 76, 131, 0.93)), url(${ribbonBackground})`,
        backgroundBlendMode: "normal",
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Paper
        elevation={20}
        sx={{
          width: 400,
          p: 6,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 3,
          background:
            "linear-gradient(145deg, rgba(255, 255, 255, 0.64) 0%, rgba(255, 255, 255, 0.28) 100%)",
          border: "1px solid rgba(255, 255, 255, 0.5)",
          boxShadow:
            "0 24px 60px rgba(8, 39, 69, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.35)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
      >
        <Stack spacing={3} alignItems="center" sx={{ width: "100%" }}>
          <ClearCallLogoTitle size={150} />
          {isCurrentUserLoading ? (
            <Stack spacing={2} alignItems="center" color="common.white">
              <CircularProgress color="inherit" />
            </Stack>
          ) : newPasswordRequired ? (
            <NewPasswordChallengeForm
              onSubmit={redirectOnNewPasswordChallenge}
              email={email}
              isSubmitting={isSubmitting}
            />
          ) : !hasAuthenticated ? (
            <>
              <LoginForm
                onSubmit={redirectOnAuthenticateUser}
                submissionError={submissionError}
                isSubmitting={isSubmitting}
              />
              <Typography
                variant="body2"
                color="common.white"
                sx={{
                  cursor: "pointer",
                  textDecoration: "underline",
                  "&:hover": {
                    opacity: 0.8,
                    textDecoration: "underline",
                  },
                }}
                onClick={() => navigate("/forgotpassword")}
              >
                Forgot Password?
              </Typography>
            </>
          ) : requiresUserDetails ? (
            <Stack direction="column" spacing={2} sx={{ width: "100%" }}>
              <Typography variant="h4" textAlign="center">
                Set Details
              </Typography>
              <UserDetailsForm
                updateText="Continue"
                onSuccess={onSetDetailsSuccess}
                onError={onSetDetailsError}
                requireUpdate={false}
              />
            </Stack>
          ) : null}
        </Stack>
      </Paper>
      <Copyright color="rgba(255, 255, 255, 0.7)" sx={{ mt: 5 }} />
    </Stack>
  );
};

export default LoginPage;
